import React, { useEffect, useState } from "react";
import {
  getFirestore,
  doc,
  onSnapshot,
  updateDoc,
  arrayUnion,
  collection,
  query,
  where,
  limit,
  getDocs,
  getDoc
} from "firebase/firestore";
import app from "../../firebase";
import { getActiveSemester, getAnnouncementSchoolYear } from "../../utils/announcementDates";

export default function CourseLineup({ studentId, onSubmit }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [student, setStudent] = useState(null);
  const [resolvedRef, setResolvedRef] = useState(null);

  useEffect(() => {
    if (!studentId) return;
    setLoading(true);
    setError("");

    const db = getFirestore(app);
    let unsub = null;

    (async () => {
      try {
        // 1) try as direct document id
        const directRef = doc(db, "students", String(studentId).trim());
        const directSnap = await getDoc(directRef);
        let docRef = null;

        if (directSnap.exists()) {
          docRef = directRef;
        } else {
          // 2) try query by idNumber field
          const q = query(collection(db, "students"), where("idNumber", "==", String(studentId).trim()), limit(1));
          const qSnap = await getDocs(q);
          if (!qSnap.empty) {
            docRef = doc(db, "students", qSnap.docs[0].id);
          }
        }

        if (!docRef) {
          setCourses([]);
          setError("Student not found.");
          setLoading(false);
          return;
        }

        setResolvedRef(docRef);

        unsub = onSnapshot(docRef, (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            setStudent({ id: snap.id, ...data });
            
            // Read from termSchedules - use the first available key if exists
            const termSchedules = data.termSchedules || {};
            const availableKeys = Object.keys(termSchedules);
            
            // Get the most recent term key (or use first available)
            const termKey = availableKeys.length > 0 ? availableKeys[availableKeys.length - 1] : null;
            const scheduleArray = termKey ? (termSchedules[termKey] || []) : [];
            
            console.log('CourseLineup Debug:', { 
              availableKeys, 
              selectedKey: termKey,
              foundSchedules: scheduleArray.length,
              studentAY: data.AY,
              studentSemester: data.semester,
              preRegSemester: data.preRegistration?.semester
            });
            
            // Resolve instructor values to display names. Try several strategies per-entry:
            //  - if `prof` looks like an email, query `users` by `email` equality
            //  - if `professorId` or `prof` looks like a user id, try getDoc(users/id)
            //  - fallback: prettify local-part of email into a display name
            (async () => {
              try {
                const db2 = getFirestore(app);
                const resolved = [];

                for (const c of scheduleArray || []) {
                  let profName = null;
                  const profVal = c.prof;

                  // 1) email -> query by email
                  if (typeof profVal === 'string' && profVal.includes('@')) {
                    try {
                      const q = query(collection(db2, 'users'), where('email', '==', String(profVal).trim()), limit(1));
                      const snap = await getDocs(q);
                      if (!snap.empty) {
                        const u = snap.docs[0].data() || {};
                        profName = u.name || (u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.email);
                      }
                    } catch (err) {
                      // ignore per-entry failures
                    }
                  }

                  // 2) professorId field or prof as id -> lookup by doc id
                  if (!profName && (c.professorId || (typeof profVal === 'string' && profVal && !profVal.includes('@')))) {
                    const candidateId = c.professorId || String(profVal);
                    try {
                      const userSnap = await getDoc(doc(db2, 'users', String(candidateId)));
                      if (userSnap.exists()) {
                        const u = userSnap.data() || {};
                        profName = u.name || (u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.email || null);
                      }
                    } catch (err) {
                      // ignore
                    }
                  }

                  // 3) fallback: prettify email local part
                  if (!profName && typeof profVal === 'string' && profVal.includes('@')) {
                    const local = String(profVal).split('@')[0];
                    profName = local.split(/[^A-Za-z0-9]+/).filter(Boolean).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
                  }

                  resolved.push({ ...c, profName: profName || profVal });
                }

                setCourses(resolved);
              } catch (e) {
                setCourses(scheduleArray);
              }
            })();

            // calculate totalUnits
            const totalUnits = Array.isArray(scheduleArray)
              ? Object.values(
                  scheduleArray.reduce((acc, course) => {
                    const key = [course.courseId, course.courseName, course.section].join('|');
                    if (!acc[key]) acc[key] = { units: parseFloat(course.units) || 0 };
                    return acc;
                  }, {})
                ).reduce((sum, row) => sum + (parseFloat(row.units) || 0), 0)
              : 0;

            if (data.totalUnits !== totalUnits) {
              // update without breaking the listener (fire-and-forget)
              updateDoc(docRef, { totalUnits }).catch(() => {});
            }
          } else {
            setCourses([]);
            setError("Student not found.");
          }
          setLoading(false);
        }, (err) => {
          setError("Failed to fetch course lineup.");
          setLoading(false);
        });
      } catch (err) {
        console.error("CourseLineup resolve error:", err);
        setError("Failed to load student.");
        setLoading(false);
      }
    })();

    return () => {
      if (unsub) unsub();
    };
  }, [studentId]);

  if (loading) return <div>Loading course lineup...</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  if (!Array.isArray(courses) || courses.length === 0) return (
    <div style={{ width: "100%", display: "flex", justifyContent: "center", paddingTop: 40, paddingBottom: 40, boxSizing: "border-box" }}>
      <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 16px rgba(0,0,0,0.08)", padding: "28px 20px", width: "100%", maxWidth: 900, boxSizing: "border-box", textAlign: "center" }}>
        <h2 style={{ color: "#18803b", fontWeight: 700, marginBottom: 12 }}>Course Line-up</h2>
        <div style={{ fontSize: 16, color: "#222" }}>Please wait for your Course Line-up.</div>
      </div>
    </div>
  );

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 40, paddingBottom: 40, boxSizing: "border-box" }}>
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
          padding: "28px 20px",
          width: "100%",
          maxWidth: 1200,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center"
        }}
      >
        <h2 style={{ color: "#18803b", fontWeight: 700, marginBottom: 12, textAlign: "center" }}>Course Line-up</h2>

        {/* responsive table wrapper: allows horizontal scroll on small screens */}
        <div style={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={{ width: "100%", minWidth: 820, borderCollapse: "collapse", marginTop: 12 }}>
            <thead>
              <tr style={{ background: "#f8fafb" }}>
                <th style={{ padding: "10px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "center", whiteSpace: "nowrap" }}>Subject Code</th>
                <th style={{ padding: "10px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "left", whiteSpace: "nowrap" }}>Subject Description</th>
                <th style={{ padding: "10px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "center", whiteSpace: "nowrap" }}>Section</th>
                <th style={{ padding: "10px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "center", whiteSpace: "nowrap" }}>Day</th>
                <th style={{ padding: "10px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "center", whiteSpace: "nowrap" }}>Schedule</th>
                <th style={{ padding: "10px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "center", whiteSpace: "nowrap" }}>Room</th>
                <th style={{ padding: "10px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "center", whiteSpace: "nowrap" }}>Instructor</th>
                <th style={{ padding: "10px 8px", borderBottom: "1px solid #e5e7eb", textAlign: "right", whiteSpace: "nowrap" }}>Units</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(
                courses.reduce((acc, course) => {
                  const key = [course.courseId, course.courseName, course.section].join('|');
                  if (!acc[key]) acc[key] = {
                    courseId: course.courseId,
                    courseName: course.courseName,
                    section: course.section,
                    units: course.units,
                    meetings: []
                  };
                  acc[key].meetings.push({
                    day: course.day,
                    time: course.time,
                    room: course.room,
                    prof: course.profName || course.prof
                  });
                  return acc;
                }, {})
              ).map((row, idx) => (
                <tr key={row.courseId + '-' + row.section} style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center", wordBreak: "break-word" }}>{row.courseId}</td>
                  <td style={{ padding: "8px", verticalAlign: "top", wordBreak: "break-word" }}>{row.courseName}</td>
                  <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center", wordBreak: "break-word" }}>{row.section}</td>
                  <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center", wordBreak: "break-word" }}>
                    {row.meetings.map((m, i) => (
                      <div key={i} style={{ whiteSpace: "pre-line" }}>{m.day}</div>
                    ))}
                  </td>
                  <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center", wordBreak: "break-word" }}>
                    {row.meetings.map((m, i) => (
                      <div key={i} style={{ whiteSpace: "pre-line" }}>{m.time}</div>
                    ))}
                  </td>
                  <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center", wordBreak: "break-word" }}>
                    {row.meetings.map((m, i) => (
                      <div key={i}>{m.room}</div>
                    ))}
                  </td>
                  <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center", wordBreak: "break-word" }}>
                    {row.meetings.map((m, i) => (
                      <div key={i}>{m.prof}</div>
                    ))}
                  </td>
                  <td style={{ padding: "8px", verticalAlign: "top", textAlign: "right", wordBreak: "break-word" }}>{row.units}</td>
                </tr>
              ))}
              {/* TOTAL UNITS row */}
              <tr>
                <td colSpan={7} style={{ border: '1px solid #e5e7eb', padding: 8, fontWeight: 'bold', textAlign: 'right' }}>TOTAL UNITS</td>
                <td style={{ border: '1px solid #e5e7eb', padding: 8, fontWeight: 'bold', textAlign: 'right' }}>
                  {Object.values(
                    courses.reduce((acc, course) => {
                      const key = [course.courseId, course.courseName, course.section].join('|');
                      if (!acc[key]) acc[key] = { units: parseFloat(course.units) || 0 };
                      return acc;
                    }, {})
                  ).reduce((sum, row) => sum + (parseFloat(row.units) || 0), 0).toFixed(1)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Instructions and Submit Button Section */}
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 36, marginBottom: 40 }}>
        <div style={{ background: '#f8fafb', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', border: '1px solid #e5e7eb', padding: '20px', maxWidth: 900, width: '100%', fontSize: 16, color: '#222', marginBottom: 14, textAlign: 'center' }}>
          Review carefully the course line-up created by your department.<br />
          If you want changes in your course line-up, contact/email your department for revisions.<br />
          Click the <b>SUBMIT</b> button once you already finalized your course line-up.
        </div>
        {Array.isArray(courses) && courses.length > 0 && resolvedRef && (
          <button
            style={{
              background: '#e67e22',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '10px 28px',
              fontWeight: 600,
              fontSize: 14,
              letterSpacing: 1,
              boxShadow: '0 2px 8px rgba(39,174,96,0.07)',
              cursor: 'pointer',
              marginTop: 8
            }}
            onClick={async () => {
              try {
                const db = getFirestore(app);
                const nowIso = new Date().toISOString();

                // Fetch system fees
                const feesRef = doc(db, "systemSettings", "miscellaneousFees");
                const feesSnap = await getDoc(feesRef);
                const feesData = feesSnap.exists() ? feesSnap.data() : {};

                // Fetch announcement for semester context
                const announcementRef = doc(db, "systemSettings", "announcement");
                const announcementSnap = await getDoc(announcementRef);
                const announcementData = announcementSnap.exists() ? announcementSnap.data() : {};

                const activeSemester = getActiveSemester(announcementData);
                const isSecondSem = activeSemester === "second";

                let semesterLabel = "1st Semester";
                let schoolYearLabel = "AY XXXX-XXXX";
                let schoolYearKey = "XXXX-XXXX";
                if (isSecondSem) {
                  semesterLabel = "2nd Semester";
                  const schoolYear = getAnnouncementSchoolYear(announcementData, "second");
                  if (schoolYear) {
                    schoolYearLabel = `AY ${schoolYear}`;
                    schoolYearKey = schoolYear;
                  }
                } else {
                  const schoolYear = getAnnouncementSchoolYear(announcementData, "first");
                  if (schoolYear) {
                    schoolYearLabel = `AY ${schoolYear}`;
                    schoolYearKey = schoolYear;
                  }
                }

                const selectedFees = feesData.fees || [];
                const cashDiscount = isSecondSem
                  ? (feesData.secondSemCashDiscount ?? feesData.cashDiscount ?? 0)
                  : (feesData.cashDiscount ?? 0);
                const cashWithDiscountDate = isSecondSem
                  ? (feesData.secondSemCashWithDiscountDate || "")
                  : (feesData.firstSemCashWithDiscountDate || feesData.cashWithDiscountDate || "");
                const cashWithoutDiscountDate = isSecondSem
                  ? (feesData.secondSemCashWithoutDiscountDate || "")
                  : (feesData.firstSemCashWithoutDiscountDate || feesData.cashWithoutDiscountDate || "");
                const installmentDates = isSecondSem
                  ? (feesData.secondSemInstallmentDates || [])
                  : (feesData.firstSemInstallmentDates || feesData.installmentDates || []);

                const totalUnits = Array.isArray(courses)
                  ? Object.values(
                      courses.reduce((acc, course) => {
                        const key = [course.courseId, course.courseName, course.section].join('|');
                        if (!acc[key]) acc[key] = { units: parseFloat(course.units) || 0 };
                        return acc;
                      }, {})
                    ).reduce((sum, row) => sum + (parseFloat(row.units) || 0), 0)
                  : 0;

                const totalAmount = Array.isArray(selectedFees)
                  ? selectedFees.reduce((sum, fee) => {
                      if (fee.perUnit !== undefined) return sum + (totalUnits * (parseFloat(fee.perUnit) || 0));
                      if (fee.formula && String(fee.formula).toLowerCase().includes("units") && typeof fee.amount === "number") {
                        return sum + (totalUnits * (parseFloat(fee.amount) || 0));
                      }
                      return sum + (parseFloat(fee.amount) || 0);
                    }, 0)
                  : 0;

                const totalWithDiscount = totalAmount - (parseFloat(cashDiscount) || 0);
                const installmentAmount = parseFloat((totalAmount / 5).toFixed(2));

                const semesterKeyLabel = semesterLabel === "1st Semester" ? "First Semester" : "Second Semester";
                const termKey = `${schoolYearKey}_${semesterKeyLabel}`;

                const miscFeesSnapshot = {
                  schoolYear: schoolYearLabel,
                  semester: semesterLabel,
                  createdAt: nowIso,
                  totalUnits,
                  fees: selectedFees,
                  cashDiscount,
                  cashWithDiscountDate,
                  cashWithoutDiscountDate,
                  installmentDates,
                  totalAmount,
                  totalWithDiscount,
                  installmentAmount
                };

                await updateDoc(resolvedRef, {
                  registrationStatus: "submitted",
                  registrationSubmittedAt: nowIso,
                  [`miscellaneousFeesHistory.${termKey}`]: arrayUnion(miscFeesSnapshot)
                });
                alert('Course line-up submitted!');
                if (typeof onSubmit === 'function') onSubmit();
              } catch (err) {
                console.error("Submit course lineup error:", err);
                alert('Failed to submit course line-up.');
              }
            }}
          >
            SUBMIT
          </button>
        )}
      </div>
    </div>
  );
}
